import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  type AdsAgentControls,
  type AdsNote,
  type AdsOverview,
  type AdsOverviewNode,
  deleteAdsNote,
  disableAdsAgent,
  enableAdsAgent,
  fetchAdsAgentControls,
  fetchAdsNotes,
  fetchAdsOverview,
  MAX_ADS_NOTE_CHARS,
  pauseAllAds,
  saveAdsNote,
  startAdsReview,
} from "../api.ts";
import { formatMoney } from "../adsMoney.ts";
import { type AssetRef, useAssetDeliveries } from "../assets.ts";
import { ConfirmDialog } from "../ConfirmDialog.tsx";
import { CreativeRequestsPanel } from "../CreativeRequests.tsx";
import { describeError } from "../data.tsx";
import { Icon } from "../Icon.tsx";
import {
  type DecisionFilters,
  DecisionLogView,
  readDecisionFilters,
} from "../DecisionLog.tsx";
import { RefreshAdsButton } from "../RefreshAdsButton.tsx";
import { RunHistoryView } from "../RunHistory.tsx";
import {
  adPath,
  adsDecisionsPath,
  adsReviewsPath,
  Link,
  replaceSearch,
  useLocation,
} from "../router.tsx";
import { localTime, since, startsIn } from "../schedule.ts";
import { useToast } from "../toast.tsx";
import {
  Badge,
  EmptyState,
  Facts,
  Figure,
  Meter,
  Notice,
  PageHeader,
  Panel,
  Segmented,
  Skeleton,
  type Tone,
  VisuallyHidden,
} from "../ui.tsx";

/** How often the page asks again: the pacing check itself runs hourly. */
const POLL_MS = 60_000;
/** While a review runs, the controls look again sooner, so "Run review now" comes back. */
const RUN_POLL_MS = 10_000;
/** Cells of the budget Meter: each one is a twelfth of the ceiling. */
const BUDGET_CELLS = 12;

/**
 * The Ads page (meta-ads-agent US-015): how the ads are doing at a glance.
 * The header carries the agent's state and the operator's controls (US-014),
 * then the latest review's summary, the month against the ceiling (US-007),
 * the KPI row and the campaign → ad set → ad table. Everything is read from
 * `GET /api/ads/overview`, which is what the sync and the reviews stored, so
 * nothing here calls Meta; **Refresh** does that on demand.
 */
export function Ads() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab: AdsTab = ADS_TABS.find((one) => one === params.get("tab")) ?? "overview";
  const [overview, setOverview] = useState<AdsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((signal?: AbortSignal): void => {
    fetchAdsOverview(signal)
      .then((next) => {
        setOverview(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (signal?.aborted !== true) setError(describeError(cause));
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const timer = window.setInterval(() => {
      load(controller.signal);
    }, POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load]);

  const reload = useCallback((): void => {
    load();
  }, [load]);

  if (overview === null) {
    return (
      <div className="page">
        <PageHeader title="Ads" />
        {error === null ? (
          <Skeleton lines={6} />
        ) : (
          <Notice kind="error">{error}</Notice>
        )}
      </div>
    );
  }

  if (!overview.connected) {
    return (
      <div className="page">
        <PageHeader title="Ads" subtitle="The Meta ads agent." />
        <EmptyState
          icon="graph"
          title="Meta is not connected"
          action={
            <Link className="button button--primary" href="/settings#meta">
              Open Settings → Meta Ads
            </Link>
          }
        >
          Enter the access token, ad account, Page and Pixel, then run{" "}
          <strong>Test connection</strong>. The agent&apos;s campaigns and
          numbers show here once it passes.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="page">
      <AgentHeader overview={overview} onChanged={reload} />
      {error !== null && <Notice kind="error">{error}</Notice>}
      {overview.pacing.metaUnreachable && (
        <Notice kind="error">
          <strong>The server cannot see spend.</strong> No pacing check has
          reached Meta since{" "}
          {overview.pacing.failingSince === null
            ? "three hours ago"
            : localTime(overview.pacing.failingSince)}
          , so marketingchief cannot pause or slow the campaigns at the monthly
          ceiling. Meta&apos;s campaign spend cap is now the only guard. Check
          the token and the connection in{" "}
          <Link href="/settings#meta">Settings → Meta Ads</Link>.
        </Notice>
      )}
      <Segmented<AdsTab>
        ariaLabel="Ads page"
        value={tab}
        onChange={(value) => {
          // Filters and the open review belong to their tab; leaving it drops them.
          replaceSearch(
            new URLSearchParams(value === "overview" ? {} : { tab: value }),
          );
        }}
        options={[
          { value: "overview", label: "Overview" },
          { value: "decisions", label: "Decisions" },
          { value: "reviews", label: "Reviews" },
        ]}
      />
      {tab === "overview" ? (
        <>
          <LatestReviewPanel overview={overview} />
          <NotesPanel />
          <CreativeRequestsPanel />
          <BudgetPanel overview={overview} />
          <KpiPanel overview={overview} />
          <CampaignTable overview={overview} />
        </>
      ) : tab === "reviews" ? (
        <RunHistoryView runId={params.get("run")} />
      ) : (
        <DecisionLogView
          filters={readDecisionFilters(params)}
          knownTargets={knownTargets(overview.campaigns)}
          onChange={setDecisionFilter}
        />
      )}
    </div>
  );
}

const ADS_TABS = ["overview", "decisions", "reviews"] as const;
type AdsTab = (typeof ADS_TABS)[number];

/**
 * One decision filter into the URL. An empty value is no filter and leaves
 * the URL; any change but paging goes back to the first page.
 */
function setDecisionFilter(key: keyof DecisionFilters, value: string): void {
  const next = new URLSearchParams(window.location.search);
  if (value === "" || (key === "page" && value === "1")) next.delete(key);
  else next.set(key, value);
  if (key !== "page") next.delete("page");
  replaceSearch(next);
}

/** Every object in the tree, as the target filter lists it: "Ad set · name". */
function knownTargets(
  nodes: readonly AdsOverviewNode[],
): { metaId: string; label: string }[] {
  return nodes.flatMap((node) => [
    { metaId: node.metaId, label: `${KIND_LABEL[node.kind]} · ${node.name}` },
    ...knownTargets(node.children),
  ]);
}

/* ---------------------------------------------------------------- header */

/**
 * The page header: the agent's state, when it last and next reviews, and the
 * operator's switches. "Enable agent" stays disabled with a list of what is
 * missing until the server says it is ready; "Pause all ads" asks first, and
 * a campaign Meta would not pause stays in a Notice with "Try again" (the
 * server keeps those, so they survive a reload).
 */
function AgentHeader({
  overview,
  onChanged,
}: {
  readonly overview: AdsOverview;
  readonly onChanged: () => void;
}) {
  const toast = useToast();
  const [controls, setControls] = useState<AdsAgentControls | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<
    "enable" | "disable" | "pause" | "review" | null
  >(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback((signal?: AbortSignal): void => {
    fetchAdsAgentControls(signal)
      .then((next) => {
        setControls(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (signal?.aborted !== true) setError(describeError(cause));
      });
  }, []);

  const running = controls?.activeRun != null;
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const timer = window.setInterval(
      () => {
        load(controller.signal);
      },
      running ? RUN_POLL_MS : POLL_MS,
    );
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load, running]);

  // A run that just finished has a new summary and a new schedule.
  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (wasRunning === running) return;
    setWasRunning(running);
    if (!running) onChanged();
  }, [running, wasRunning, onChanged]);

  const act = <T,>(
    kind: NonNullable<typeof busy>,
    action: () => Promise<T>,
    done: (result: T) => void,
  ): void => {
    setBusy(kind);
    action()
      .then((result) => {
        done(result);
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
        setConfirming(false);
        load();
        onChanged();
      });
  };

  const enable = (): void => {
    act("enable", enableAdsAgent, (result) => {
      setControls(result);
      const failed = result.resumed.filter(
        (decision) => decision.outcome !== "executed",
      ).length;
      if (failed > 0)
        toast.error(
          `The agent is enabled, but Meta did not resume ${String(failed)} campaign${failed === 1 ? "" : "s"}.`,
        );
      else
        toast.ok(
          result.resumed.length > 0
            ? "Agent enabled: the paused campaign is resumed and a review started."
            : "Agent enabled: the launch review started.",
        );
    });
  };
  const disable = (): void => {
    act("disable", disableAdsAgent, (result) => {
      setControls(result);
      toast.ok(
        "Agent disabled. The ads keep running and the hourly ceiling check goes on.",
      );
    });
  };
  const pause = (): void => {
    act("pause", pauseAllAds, (result) => {
      setControls(result);
      if (result.pauseFailures.length === 0) {
        toast.ok(
          result.decisions.length === 0
            ? "Agent disabled. No campaign was running."
            : "All ads paused and the agent disabled.",
        );
      }
    });
  };
  const review = (): void => {
    act("review", startAdsReview, () => {
      toast.ok("Review started.");
    });
  };

  const enabled = controls?.enabled ?? false;
  const ready = controls?.readiness.ready ?? false;
  const loaded = controls !== null;

  return (
    <>
      <PageHeader
        title="Ads"
        subtitle={<AgentStatusLine overview={overview} controls={controls} />}
        actions={
          <>
            <RefreshAdsButton disabled={running} onRefreshed={onChanged} />
            {enabled ? (
              <button
                type="button"
                className="button"
                onClick={disable}
                disabled={busy !== null}
              >
                {busy === "disable" ? "Disabling…" : "Disable agent"}
              </button>
            ) : (
              <button
                type="button"
                className="button button--primary"
                onClick={enable}
                disabled={!loaded || busy !== null || !ready || running}
                title={
                  running
                    ? "A review is running; enable the agent once it has finished."
                    : undefined
                }
              >
                {busy === "enable" ? "Enabling…" : "Enable agent"}
              </button>
            )}
            <button
              type="button"
              className="button"
              onClick={review}
              disabled={!loaded || busy !== null || running}
              title={
                running
                  ? "A review is running."
                  : "Start a review now, outside the 12-hour schedule"
              }
            >
              {busy === "review" ? "Starting…" : "Run review now"}
            </button>
            <button
              type="button"
              className="button button--danger"
              onClick={() => {
                setConfirming(true);
              }}
              disabled={!loaded || busy !== null}
            >
              Pause all ads
            </button>
          </>
        }
      />
      {error !== null && <Notice kind="error">{error}</Notice>}
      {overview.connectionStatus === "token_invalid" && (
        <Notice kind="error">
          <strong>Meta refused the access token.</strong> Nothing can be read or
          changed until a new token is saved in{" "}
          <Link href="/settings#meta">Settings → Meta Ads</Link>.
        </Notice>
      )}
      {controls !== null && controls.pauseFailures.length > 0 && (
        <Notice kind="error" role="alert">
          <strong>Meta did not pause every campaign.</strong> The agent is
          disabled, but{" "}
          {controls.pauseFailures
            .map(
              (failure) =>
                `${failure.campaignName} (${failureMessage(failure.decision.outcomeDetail)})`,
            )
            .join("; ")}{" "}
          {controls.pauseFailures.length === 1 ? "is" : "are"} still running.{" "}
          <button
            type="button"
            className="button button--small"
            onClick={pause}
            disabled={busy !== null}
          >
            {busy === "pause" ? "Pausing…" : "Try again"}
          </button>
        </Notice>
      )}
      {controls !== null && !controls.enabled && !controls.readiness.ready && (
        <Notice kind="info">
          Before the agent can be enabled:
          <ul>
            {controls.readiness.missing.map((item) => (
              <li key={item.key}>{item.message}</li>
            ))}
          </ul>
          <Link href="/settings#meta">Open Settings → Meta Ads</Link>
        </Notice>
      )}
      {controls !== null &&
        !controls.enabled &&
        controls.readiness.ready &&
        controls.pausedByOperator > 0 && (
          <Notice kind="info">
            &quot;Pause all ads&quot; paused {String(controls.pausedByOperator)}{" "}
            campaign{controls.pausedByOperator === 1 ? "" : "s"}. Enabling the
            agent resumes {controls.pausedByOperator === 1 ? "it" : "them"}{" "}
            first, so Meta keeps what it has learned.
          </Notice>
        )}
      <ConfirmDialog
        open={confirming}
        title="Pause all ads?"
        confirmLabel="Pause all ads"
        busyLabel="Pausing…"
        danger
        busy={busy === "pause"}
        onConfirm={pause}
        onCancel={() => {
          setConfirming(false);
        }}
      >
        <p>
          Every running agent campaign is paused in Meta right away, and the
          agent is disabled so no review turns them back on. Enabling the agent
          later resumes them.
        </p>
      </ConfirmDialog>
    </>
  );
}

/**
 * The badges, then the schedule. More than one badge can hold at once: an
 * enabled agent whose campaigns the ceiling check paused is both.
 */
function AgentStatusLine({
  overview,
  controls,
}: {
  readonly overview: AdsOverview;
  readonly controls: AdsAgentControls | null;
}) {
  // Re-render every 30 s so the countdown moves between the polls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const running = controls?.activeRun != null;
  const enabled = controls?.enabled ?? false;
  const next = nextReview(overview);
  return (
    <span className="ads-status">
      {overview.connectionStatus === "token_invalid" && (
        <Badge tone="danger">Token invalid</Badge>
      )}
      {controls === null ? null : running ? (
        <Badge tone="active" pulse>
          Running
        </Badge>
      ) : enabled ? (
        <Badge tone="ready">Enabled</Badge>
      ) : (
        <Badge>Disabled</Badge>
      )}
      {overview.pausedByCeiling > 0 && (
        <Badge
          tone="wait"
          title="The hourly check paused the campaigns: this month's spend reached the ceiling."
        >
          Paused by ceiling
        </Badge>
      )}
      <span className="ads-status__text">
        {overview.lastRun?.finishedAt == null
          ? "No review yet"
          : `Last review ${since(overview.lastRun.finishedAt, now)}${overview.lastRun.status === "succeeded" ? "" : ` (${runStatusLabel(overview.lastRun.status)})`}`}
        {" · "}
        {!enabled || next === null
          ? "no review scheduled"
          : `${next.retry ? "retry" : "next review"} ${startsIn(next.at, now).replace(/^starts /u, "")}`}
      </span>
    </span>
  );
}

/** The sooner of the retry and the regular slot. */
function nextReview(
  overview: AdsOverview,
): { readonly at: string; readonly retry: boolean } | null {
  const { nextReviewAt, retryAt } = overview;
  if (retryAt !== null && (nextReviewAt === null || retryAt < nextReviewAt))
    return { at: retryAt, retry: true };
  return nextReviewAt === null ? null : { at: nextReviewAt, retry: false };
}

function runStatusLabel(status: string): string {
  if (status === "held_usage_limit") return "held by the usage limit";
  return status;
}

/* ---------------------------------------------------------------- panels */

function LatestReviewPanel({ overview }: { readonly overview: AdsOverview }) {
  const run = overview.latestSummary;
  return (
    <Panel
      title="Latest review"
      icon="comment"
      meta={
        run?.finishedAt == null ? undefined : (
          <span className="muted">{localTime(run.finishedAt)}</span>
        )
      }
      actions={
        run === null ? undefined : (
          <Link className="button button--quiet" href={adsReviewsPath(run.id)}>
            Open review
          </Link>
        )
      }
    >
      {run === null ? (
        <p className="muted">
          No review has come back yet. The first one runs when the agent is
          enabled.
        </p>
      ) : (
        <>
          <p>{run.summary}</p>
          {run.observations.length > 0 && (
            <ul className="ads-observations">
              {run.observations.map((observation, index) => (
                <li key={index}>{observation}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * Notes for the agent (US-018): steer it without changing a setting. A note
 * waits until the next review reads it, then names that review. The agent
 * answers it in that review's summary or observations.
 */
function NotesPanel() {
  const toast = useToast();
  const [notes, setNotes] = useState<AdsNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback((signal?: AbortSignal): void => {
    fetchAdsNotes(signal)
      .then((next) => {
        setNotes(next.notes);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (signal?.aborted !== true) setError(describeError(cause));
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    // A review may read the waiting notes at any time.
    const timer = window.setInterval(() => {
      load(controller.signal);
    }, POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load]);

  const save = (): void => {
    setBusy(true);
    saveAdsNote(draft)
      .then(() => {
        setDraft("");
        toast.ok("Note saved for the next review.");
        load();
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const remove = (id: string): void => {
    deleteAdsNote(id)
      .then(() => {
        load();
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
        load();
      });
  };

  const waiting = notes?.filter((note) => note.consumedBy === null).length ?? 0;
  return (
    <Panel
      title="Notes for the agent"
      icon="comment"
      meta={
        waiting === 0 ? undefined : (
          <Badge tone="wait">{String(waiting)} waiting</Badge>
        )
      }
    >
      <form
        className="ads-notes__form"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="ads-note">
            New note
          </label>
          <textarea
            id="ads-note"
            className="field__input field__textarea"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            rows={3}
            maxLength={MAX_ADS_NOTE_CHARS}
            placeholder="We launch the new pricing on Monday, push that."
            disabled={busy}
          />
          <p className="field__hint">
            The next review reads it as context and wishes, and answers it in
            its summary. A note never overrides the agent&apos;s rules, the
            budget ceiling or the guardrails.
          </p>
        </div>
        <button
          type="submit"
          className="button button--primary"
          disabled={busy || draft.trim() === ""}
        >
          Save note
        </button>
      </form>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {notes !== null && notes.length > 0 && (
        <ul className="ads-notes">
          {notes.map((note) => (
            <li key={note.id} className="ads-notes__item">
              <p className="ads-notes__body">{note.body}</p>
              <div className="ads-notes__meta">
                <span className="muted">{localTime(note.createdAt)}</span>
                {note.consumedBy === null ? (
                  <>
                    <Badge tone="wait">Waiting for next review</Badge>
                    <button
                      type="button"
                      className="button button--quiet button--danger"
                      onClick={() => {
                        remove(note.id);
                      }}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <Link href={adsReviewsPath(note.consumedBy.id)}>
                    Read by the review of{" "}
                    {note.consumedBy.startedAt === null
                      ? "an earlier run"
                      : localTime(note.consumedBy.startedAt)}
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function BudgetPanel({ overview }: { readonly overview: AdsOverview }) {
  const { budget, pacing } = overview;
  const money = (minor: number | null): string =>
    minor === null ? "Not set" : formatMoney(minor, budget.currency);
  const share =
    budget.ceilingMinor === null || budget.ceilingMinor === 0
      ? 0
      : budget.spentMinor / budget.ceilingMinor;
  const overCeiling =
    budget.ceilingMinor !== null && budget.projectedMinor > budget.ceilingMinor;
  return (
    <Panel
      title="This month"
      icon="graph"
      meta={
        <span className="muted">{`${budget.month} · ${budget.timezone}`}</span>
      }
    >
      <div className="ads-budget">
        <div className="ads-budget__line">
          <strong>{formatMoney(budget.spentMinor, budget.currency)}</strong>
          <span className="muted"> of {money(budget.ceilingMinor)} spent</span>
        </div>
        <Meter
          value={Math.round(Math.min(1, share) * BUDGET_CELLS)}
          max={BUDGET_CELLS}
          label={`${String(Math.round(share * 100))}% of the monthly ceiling spent`}
        />
      </div>
      <Facts
        items={[
          {
            label: "Current daily budget",
            value: money(budget.dailyBudgetsMinor),
          },
          {
            label: "Projected month-end spend",
            value: (
              <>
                {money(budget.projectedMinor)}
                {overCeiling && (
                  <span className="muted">
                    {" "}
                    — above the ceiling, so pacing slows or pauses the campaigns
                    first
                  </span>
                )}
              </>
            ),
          },
          { label: "Remaining", value: money(budget.remainingMinor) },
          { label: "Most per day", value: money(budget.maxDailyBudgetMinor) },
          { label: "Days left", value: String(budget.daysLeft) },
          {
            label: "Last pacing check",
            value:
              pacing.lastPacingAt === null
                ? "Not yet"
                : localTime(pacing.lastPacingAt),
          },
        ]}
      />
    </Panel>
  );
}

function KpiPanel({ overview }: { readonly overview: AdsOverview }) {
  const { last7d, previous7d, last30d } = overview.kpis;
  const money = (minor: number | null): string =>
    minor === null ? "—" : formatMoney(minor, overview.currency);
  const previous = (value: string): string => `previous: ${value}`;
  const target = overview.targetCostMinor;
  const costTone: Tone | undefined =
    last30d.costPerBookedCallMinor === null || target === null
      ? undefined
      : last30d.costPerBookedCallMinor <= target
        ? "done"
        : "danger";
  const proxy = overview.proxyEventLabel ?? "Proxy events";
  return (
    <Panel
      title="Last 7 days vs the previous 7"
      icon="pulse"
      meta={
        last7d.incompleteAttribution ? (
          <span
            className="muted"
            title="Meta still attributes conversions to the last 48 hours; these counts can still go up."
          >
            attribution still coming in
          </span>
        ) : undefined
      }
    >
      <div className="figures">
        <Figure
          label="Spend"
          value={money(last7d.spendMinor)}
          hint={previous(money(previous7d.spendMinor))}
        />
        <Figure
          label={proxy}
          value={String(last7d.proxyEvents)}
          hint={previous(String(previous7d.proxyEvents))}
        />
        <Figure
          label={`Cost per ${overview.proxyEventLabel ?? "proxy event"}`}
          value={money(last7d.costPerProxyEventMinor)}
          hint={previous(money(previous7d.costPerProxyEventMinor))}
        />
        <Figure
          label="Link CTR"
          value={percent(last7d.linkCtr)}
          hint={previous(percent(previous7d.linkCtr))}
        />
        <Figure
          label="Cost per landing page view"
          value={money(last7d.costPerLandingPageViewMinor)}
          hint={previous(money(previous7d.costPerLandingPageViewMinor))}
        />
      </div>
      <h3 className="ads-kpi__heading">Last 30 days</h3>
      <div className="figures">
        <Figure
          label="Booked calls"
          value={String(last30d.bookedCalls)}
          hint={`${overview.bookedCallLabel} · a 7-day count is too small to read`}
        />
        <Figure
          label="Cost per booked call"
          value={money(last30d.costPerBookedCallMinor)}
          {...(costTone === undefined ? {} : { tone: costTone })}
          hint={target === null ? "no target set" : `target ${money(target)}`}
        />
      </div>
    </Panel>
  );
}

/* ----------------------------------------------------------------- table */

interface Row {
  readonly node: AdsOverviewNode;
  readonly depth: number;
}

function CampaignTable({ overview }: { readonly overview: AdsOverview }) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (metaId: string): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(metaId)) next.delete(metaId);
      else next.add(metaId);
      return next;
    });
  };

  const rows = useMemo(() => {
    const out: Row[] = [];
    const walk = (nodes: readonly AdsOverviewNode[], depth: number): void => {
      for (const node of nodes) {
        out.push({ node, depth });
        if (!collapsed.has(node.metaId)) walk(node.children, depth + 1);
      }
    };
    walk(overview.campaigns, 0);
    return out;
  }, [overview.campaigns, collapsed]);

  const refs = useMemo(() => {
    const out: AssetRef[] = [];
    const walk = (nodes: readonly AdsOverviewNode[]): void => {
      for (const node of nodes) {
        if (node.thumbnail !== null)
          out.push({
            contentId: node.thumbnail.contentId,
            assetId: node.thumbnail.assetId,
          });
        walk(node.children);
      }
    };
    walk(overview.campaigns);
    return out;
  }, [overview.campaigns]);
  const thumbnails = useAssetDeliveries(refs);

  if (overview.campaigns.length === 0) {
    return (
      <Panel title="Campaigns" icon="list">
        <p className="muted">
          The agent has not made a campaign yet. Its launch review builds the
          first one.
        </p>
      </Panel>
    );
  }

  const money = (minor: number | null): string =>
    minor === null ? "—" : formatMoney(minor, overview.currency);
  const target = overview.targetCostMinor;
  return (
    <Panel
      title="Campaigns"
      icon="list"
      meta={<span className="muted">last 30 days</span>}
    >
      <div className="table-wrap">
        <table className="table table--cards ads-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Age</th>
              <th className="num">Spend</th>
              <th className="num">Booked calls</th>
              <th className="num">Cost per booked call</th>
              <th className="num">CTR</th>
              <th className="num">Frequency</th>
              <th>
                <VisuallyHidden>Ads Manager</VisuallyHidden>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ node, depth }) => {
              const url =
                node.thumbnail === null
                  ? undefined
                  : thumbnails.get(node.thumbnail.assetId)?.url;
              const cost = node.metrics.costPerBookedCallMinor;
              return (
                <tr key={node.metaId}>
                  <td className="table__cell--lead">
                    <span
                      className={`ads-table__name ads-table__name--depth-${String(depth)}`}
                    >
                      {node.children.length > 0 ? (
                        <button
                          type="button"
                          className="button button--icon button--quiet"
                          aria-expanded={!collapsed.has(node.metaId)}
                          aria-label={`${collapsed.has(node.metaId) ? "Show" : "Hide"} what is under ${node.name}`}
                          onClick={() => {
                            toggle(node.metaId);
                          }}
                        >
                          <Icon
                            name={
                              collapsed.has(node.metaId)
                                ? "chevron-right"
                                : "chevron-down"
                            }
                          />
                        </button>
                      ) : (
                        <span className="ads-table__spacer" />
                      )}
                      {node.kind === "ad" && (
                        <Thumbnail node={node} url={url} />
                      )}
                      <span className="ads-table__label">
                        <span className="ads-table__kind">
                          {KIND_LABEL[node.kind]}
                        </span>
                        <Link
                          className="link link--strong"
                          href={
                            node.kind === "ad"
                              ? adPath(node.metaId)
                              : adsDecisionsPath(node.metaId)
                          }
                          title={
                            node.kind === "ad"
                              ? `Open ${node.name}`
                              : `Decisions about ${node.name}`
                          }
                        >
                          {node.name}
                        </Link>
                      </span>
                    </span>
                  </td>
                  <td data-label="Status">
                    <ObjectStatus node={node} />
                  </td>
                  <td data-label="Age" className="table__nowrap">
                    {age(node.ageHours)}
                  </td>
                  <td data-label="Spend" className="num">
                    {money(node.metrics.spendMinor)}
                  </td>
                  <td data-label="Booked calls" className="num">
                    {node.metrics.bookedCalls}
                  </td>
                  <td
                    data-label="Cost per booked call"
                    className={`num${cost === null || target === null ? "" : cost <= target ? " text-done" : " text-danger"}`}
                  >
                    {money(cost)}
                  </td>
                  <td data-label="CTR" className="num">
                    {percent(node.metrics.linkCtr)}
                  </td>
                  <td data-label="Frequency" className="num">
                    {node.frequency === null ? "—" : node.frequency.toFixed(2)}
                  </td>
                  <td className="table__cell--foot">
                    <a
                      className="link"
                      href={adsManagerUrl(overview.adAccountId, node)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Ads Manager <Icon name="link-external" />
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

const KIND_LABEL: Readonly<Record<AdsOverviewNode["kind"], string>> = {
  campaign: "Campaign",
  adset: "Ad set",
  ad: "Ad",
};

function Thumbnail({
  node,
  url,
}: {
  readonly node: AdsOverviewNode;
  readonly url: string | undefined;
}) {
  if (url === undefined) {
    return (
      <span
        className="ads-thumb ads-thumb--empty"
        title="No picture in the bucket for this creative"
      >
        <Icon
          name={
            node.thumbnail?.video === true ? "device-camera-video" : "image"
          }
        />
      </span>
    );
  }
  return (
    <span className="ads-thumb">
      <img src={url} alt={`Creative of ${node.name}`} loading="lazy" />
    </span>
  );
}

/** Meta's own word for where the object is, with who paused it. */
export function ObjectStatus({
  node,
}: {
  readonly node: Pick<AdsOverviewNode, "status" | "effectiveStatus" | "pausedBy">;
}) {
  const status = node.effectiveStatus ?? node.status;
  let tone: Tone = "neutral";
  let label: ReactNode = status === null ? "Unknown" : sentence(status);
  if (status === "ACTIVE") tone = "ready";
  else if (status === "PENDING_REVIEW") {
    tone = "review";
    label = "In review";
  } else if (status === "IN_PROCESS" || status === "PREAPPROVED")
    tone = "review";
  else if (status === "DISAPPROVED") {
    tone = "danger";
    label = "Rejected";
  } else if (status === "WITH_ISSUES" || status === "PENDING_BILLING_INFO")
    tone = "danger";
  else if (status === "PAUSED" && node.pausedBy !== null)
    label = `Paused by ${PAUSED_BY_LABEL[node.pausedBy]}`;
  else if (status === "CAMPAIGN_PAUSED") label = "Campaign paused";
  else if (status === "ADSET_PAUSED") label = "Ad set paused";
  if (node.pausedBy === "system_ceiling") tone = "wait";
  return <Badge tone={tone}>{label}</Badge>;
}

const PAUSED_BY_LABEL: Readonly<
  Record<NonNullable<AdsOverviewNode["pausedBy"]>, string>
> = {
  agent: "the agent",
  system_ceiling: "the ceiling",
  operator: "you",
  external: "someone in Meta",
};

/** `PENDING_REVIEW` → "Pending review". */
function sentence(status: string): string {
  const words = status.toLowerCase().replace(/_/gu, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function age(hours: number | null): string {
  if (hours === null) return "Not live yet";
  return hours < 48
    ? `${String(hours)} h`
    : `${String(Math.floor(hours / 24))} d`;
}

export function percent(fraction: number | null): string {
  return fraction === null ? "—" : `${(fraction * 100).toFixed(2)}%`;
}

/** The object in Ads Manager: the account's digits, and the object selected. */
export function adsManagerUrl(
  adAccountId: string,
  node: Pick<AdsOverviewNode, "kind" | "metaId">,
): string {
  const plural =
    node.kind === "campaign"
      ? "campaigns"
      : node.kind === "adset"
        ? "adsets"
        : "ads";
  const params = new URLSearchParams({
    act: adAccountId.replace(/^act_/u, ""),
    [`selected_${node.kind}_ids`]: node.metaId,
  });
  return `https://adsmanager.facebook.com/adsmanager/manage/${plural}?${params.toString()}`;
}

function failureMessage(detail: string | null): string {
  if (detail === null) return "no answer from Meta";
  try {
    const parsed = JSON.parse(detail) as { message?: unknown };
    return typeof parsed.message === "string" ? parsed.message : detail;
  } catch {
    return detail;
  }
}
